import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, downloadBlob, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AlertOctagon, Filter, FileDown, FilePlus2, X, Trash2, Save } from "lucide-react";

const SEV_TONE = {
  critical: "bg-red-100 text-red-800 border-red-300",
  high: "bg-orange-100 text-orange-800 border-orange-300",
  medium: "bg-amber-100 text-amber-800 border-amber-300",
  low: "bg-emerald-100 text-emerald-800 border-emerald-300",
};

/** Static Care Plan templates — pre-filled fields matching the care-planning section schema. */
export const CARE_PLAN_TEMPLATES = {
  "support-plan": {
    label: "Personal Support Plan",
    description: "Person-centred day-to-day support plan for domiciliary or supported living.",
    data: {
      plan_type: "Personal Support Plan",
      title: "Person-Centred Support Plan",
      goals: "• Support the person to remain independent in the areas they choose\n• Maintain safe daily routines around hygiene, nutrition and hydration\n• Support community access and social connection\n• Uphold dignity, privacy and choice at every visit",
      interventions: "• Prompt & assist with morning/evening routines as agreed\n• Prepare balanced meals following stated preferences and any dietary needs\n• Escort to community activities per weekly plan\n• Encourage decision-making — offer choices, not directives",
      person_voice: "\"I want to be supported to…\" (record the person's own words here)",
      summary: "This support plan describes the day-to-day support the person requires to live the life they choose, in line with the Care Act 2014 well-being principles.",
      status: "draft",
    },
  },
  "end-of-life": {
    label: "End of Life Care Plan",
    description: "ReSPECT / advance-care planning with priorities of care and preferred place.",
    data: {
      plan_type: "End of Life Care Plan",
      title: "End of Life & Advance Care Plan",
      goals: "• Comfort, dignity and pain control at all times\n• Uphold the person's stated wishes about place of care and place of death\n• Support family and important people at their pace\n• Coordinate with GP, community nursing, hospice and out-of-hours services",
      interventions: "• Follow the ReSPECT / TEP form held on file\n• Anticipatory medications available and correctly stored\n• Symptom score recorded at every visit (pain, breathlessness, agitation)\n• Escalation plan: contact <named clinician> before 999 unless imminent risk\n• Spiritual/cultural preferences observed as documented",
      person_voice: "The person has said they wish to be cared for at <home / hospice> and wants <name(s)> present.",
      summary: "This plan is completed in partnership with the person, their family and clinical team. It reflects the priorities of care from the One Chance to Get It Right framework.",
      status: "active",
    },
  },
  "pbs": {
    label: "PBS (Positive Behaviour Support) Plan",
    description: "Functional analysis, proactive strategies and reactive protocols for behaviours of concern.",
    data: {
      plan_type: "Positive Behaviour Support (PBS)",
      title: "Positive Behaviour Support Plan",
      goals: "• Increase quality of life and meaningful engagement\n• Reduce the frequency, intensity and duration of behaviours of concern\n• Ensure any restrictive practice is a genuine last resort, least-restrictive option, time-limited and reviewed",
      interventions: "PROACTIVE STRATEGIES — environment, communication supports, predictable routines, sensory regulation, offering choice.\n\nSECONDARY (early warning) — recognise signs, redirect, offer break, use agreed distraction items.\n\nREACTIVE — the specific de-escalation script the team must follow. Restrictive interventions listed only where clinically approved & MDT-signed off.",
      person_voice: "Behaviours communicate a need — the person is telling us: '…' (record their voice / observed function).",
      summary: "This PBS plan follows the BILD Restraint Reduction Network Training Standards and Positive & Proactive Care guidance (DHSC 2014). Restrictive practice logs must be raised for every use.",
      status: "active",
    },
  },
  "mca-dols": {
    label: "MCA / DoLS Plan",
    description: "Capacity assessment, best-interests decision and any deprivation of liberty safeguards.",
    data: {
      plan_type: "Mental Capacity Assessment (MCA)",
      title: "Mental Capacity & Best Interests Plan",
      goals: "• Presume capacity; assess capacity only for a specific decision at a specific time\n• Where capacity is lacking, make decisions in the person's best interests using the least-restrictive option\n• Involve family, advocate (IMCA) and MDT as required",
      interventions: "CAPACITY ASSESSMENT — decision: <name it>. Assessor: <name/role>. Date: <yyyy-mm-dd>. Outcome: <has / lacks capacity for this decision>.\n\nBEST INTERESTS MEETING — attendees, options considered, decision reached, review date.\n\nDoLS AUTHORISATION — reference #, granted from → to, conditions attached.\n\nLPA / DEPUTY status recorded and copies on file.",
      person_voice: "The person's expressed wishes, feelings, beliefs and values as they relate to this decision.",
      summary: "This plan complies with the Mental Capacity Act 2005 Code of Practice and DoLS 2009 (transitioning to LPS). Review at least annually or on any material change.",
      status: "active",
    },
  },
};

function TemplatePickerDialog({ open, onClose, onCreated }) {
  const [templateKey, setTemplateKey] = useState("support-plan");
  const [customTemplates, setCustomTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [saving, setSaving] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);

  const loadTemplates = () => api.get("/care-plan-templates")
    .then((r) => setCustomTemplates(r.data || []))
    .catch(() => {/* silent — custom templates are optional */});

  useEffect(() => {
    if (!open) return;
    api.get("/clients").then((r) => setClients(r.data || [])).catch((e) => toast.error(formatApiError(e)));
    loadTemplates();
  }, [open]);

  if (!open) return null;

  // Combined list — built-in keys stay as-is; custom templates use their DB id
  const allTemplates = [
    ...Object.entries(CARE_PLAN_TEMPLATES).map(([k, t]) => ({ id: k, builtin: true, ...t })),
    ...customTemplates.map((t) => ({ id: t.id, builtin: false, label: t.label, description: t.description || "Custom template", data: t.data, created_by_name: t.created_by_name })),
  ];
  const tpl = allTemplates.find((t) => t.id === templateKey) || allTemplates[0];

  const create = async () => {
    if (!clientId) { toast.error("Choose a service user"); return; }
    if (!tpl) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/sections/care-planning`, { data: tpl.data });
      toast.success(`${tpl.label} created — open the profile to review & save`);
      onCreated?.(data);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const previewPdf = async () => {
    if (!tpl) return;
    setPreviewBusy(true);
    try {
      const token = localStorage.getItem("smartcare_token");
      const resp = await fetch(`${API_BASE}/care-plan-templates/preview.pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ label: tpl.label, data: tpl.data }),
      });
      if (!resp.ok) throw new Error(`Preview failed (${resp.status})`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `ayobell-template-${tpl.label.toLowerCase().replace(/\s+/g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message || "Preview failed"); }
    finally { setPreviewBusy(false); }
  };

  const removeCustom = async (id) => {
    if (!window.confirm("Delete this custom template? It will no longer appear in the picker.")) return;
    try {
      await api.delete(`/care-plan-templates/${id}`);
      toast.success("Template removed");
      if (templateKey === id) setTemplateKey("support-plan");
      await loadTemplates();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4" data-testid="template-dialog">
      <div className="bg-white border border-slate-200 w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">New Care Plan</div>
            <h2 className="font-heading text-lg font-semibold">Start from a template</h2>
          </div>
          <button onClick={onClose} data-testid="template-close" className="text-slate-500 hover:text-slate-900"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          <div>
            <div className="flex items-baseline justify-between">
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Built-in templates</label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2" data-testid="template-grid">
              {Object.entries(CARE_PLAN_TEMPLATES).map(([k, t]) => (
                <button
                  key={k}
                  onClick={() => setTemplateKey(k)}
                  data-testid={`template-opt-${k}`}
                  className={`border text-left p-3 transition-colors ${templateKey === k ? "border-slate-900 bg-slate-50 ring-2 ring-slate-900 ring-inset" : "border-slate-300 hover:bg-slate-50"}`}
                >
                  <div className="font-medium text-sm text-slate-900">{t.label}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{t.description}</div>
                </button>
              ))}
            </div>
          </div>

          {customTemplates.length > 0 && (
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Your custom templates</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2" data-testid="custom-template-grid">
                {customTemplates.map((t) => (
                  <div
                    key={t.id}
                    className={`border p-3 flex items-start gap-2 ${templateKey === t.id ? "border-slate-900 bg-slate-50 ring-2 ring-slate-900 ring-inset" : "border-emerald-300 bg-emerald-50/40"}`}
                    data-testid={`custom-template-${t.id}`}
                  >
                    <button onClick={() => setTemplateKey(t.id)} className="flex-1 min-w-0 text-left">
                      <div className="font-medium text-sm text-slate-900 truncate">{t.label}</div>
                      <div className="text-[11px] text-slate-500 line-clamp-2">{t.description || "Custom template"}</div>
                      {t.created_by_name && <div className="text-[10px] uppercase tracking-widest text-emerald-700 mt-0.5">by {t.created_by_name}</div>}
                    </button>
                    <button
                      onClick={() => removeCustom(t.id)}
                      className="text-slate-400 hover:text-red-600 shrink-0"
                      data-testid={`btn-delete-custom-${t.id}`}
                      title="Delete template"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Service user</label>
            <select
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              data-testid="template-client-select"
              className="w-full mt-1 h-10 px-3 border border-slate-300 bg-white text-sm"
            >
              <option value="">— choose service user —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}
            </select>
          </div>
          <div className="bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Preview — title & goals</div>
              <button
                onClick={previewPdf}
                disabled={previewBusy || !tpl}
                data-testid="btn-template-preview-pdf"
                className="text-[11px] uppercase tracking-widest text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 disabled:opacity-50"
              >
                <FileDown className="w-3 h-3" /> {previewBusy ? "Preparing…" : "Download preview PDF"}
              </button>
            </div>
            <div className="font-medium">{tpl?.data?.title || "—"}</div>
            <pre className="whitespace-pre-wrap text-[11px] text-slate-600 mt-1 font-sans">{tpl?.data?.goals || ""}</pre>
          </div>
        </div>
        <div className="border-t border-slate-200 px-5 py-3 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="template-cancel">Cancel</Button>
          <Button onClick={create} disabled={saving || !clientId || !tpl} data-testid="template-create" className="bg-slate-900 hover:bg-slate-800">
            {saving ? "Creating…" : `Create ${tpl?.label || "plan"}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function CarePlansOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [tplOpen, setTplOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/dashboard/care-plans-overview")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (status === "all") return data.plans;
    return data.plans.filter((p) => p.review_status === status);
  }, [data, status]);

  const counts = data?.counts || {};
  const CARDS = [
    { key: "overdue", label: "Overdue", tone: "bg-red-50 border-red-300 text-red-800" },
    { key: "due_soon", label: "Due next 30 days", tone: "bg-amber-50 border-amber-300 text-amber-800" },
    { key: "current", label: "Current", tone: "bg-emerald-50 border-emerald-300 text-emerald-800" },
    { key: "no_date", label: "No review date", tone: "bg-slate-50 border-slate-300 text-slate-700" },
  ];

  const exportPdf = async () => {
    setPdfBusy(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      await downloadBlob("/reports/care-plans.pdf", `ayobell-care-plans-${today}.pdf`);
      toast.success("Care Plans register downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPdfBusy(false); }
  };

  const saveAsTemplate = async (plan) => {
    const label = window.prompt(
      `Save "${plan.title}" as a reusable template. Give it a memorable name — this is what will appear in the picker for future plans.`,
      plan.title || plan.plan_type || ""
    );
    if (!label || !label.trim()) return;
    const description = window.prompt("Add a short description so colleagues know when to use this template (optional):", "");
    try {
      await api.post("/care-plan-templates/from-plan", {
        plan_id: plan.id,
        label: label.trim(),
        description: (description || "").trim(),
      });
      toast.success(`"${label}" saved as a template — it'll appear in "New from template"`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="hidden lg:flex h-14 border-b border-slate-200 bg-white px-6 items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Care Delivery</div>
          <h1 className="font-heading text-xl font-semibold" data-testid="care-plans-title">Care Plans Overview</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setTplOpen(true)} data-testid="btn-new-from-template" className="bg-slate-900 hover:bg-slate-800 h-9 text-xs">
            <FilePlus2 className="w-3.5 h-3.5 mr-1.5" /> New from template
          </Button>
          <Button onClick={exportPdf} disabled={pdfBusy} data-testid="btn-care-plans-pdf" variant="outline" className="h-9 text-xs">
            <FileDown className="w-3.5 h-3.5 mr-1.5" /> {pdfBusy ? "Exporting…" : "Export PDF"}
          </Button>
        </div>
      </div>
      {/* Mobile header — Batch 7C */}
      <div className="lg:hidden border-b border-slate-200 bg-white px-4 py-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Care Delivery</div>
            <h1 className="font-heading text-base font-semibold truncate" data-testid="care-plans-title-mobile">Care Plans Overview</h1>
          </div>
          <Button onClick={exportPdf} disabled={pdfBusy} data-testid="btn-care-plans-pdf-mobile" variant="outline" size="sm" className="h-11 shrink-0">
            <FileDown className="w-4 h-4" />
          </Button>
        </div>
        <Button onClick={() => setTplOpen(true)} data-testid="btn-new-from-template-mobile" className="bg-slate-900 hover:bg-slate-800 h-11 w-full text-sm">
          <FilePlus2 className="w-4 h-4 mr-1.5" /> New from template
        </Button>
      </div>
      <div className="p-4 lg:p-6 space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="care-plans-cards">
          {CARDS.map((c) => (
            <button key={c.key} onClick={() => setStatus(status === c.key ? "all" : c.key)} data-testid={`cp-card-${c.key}`} className={`border p-4 text-left ${c.tone} ${status === c.key ? "ring-2 ring-slate-900 ring-inset" : ""}`}>
              <div className="text-[10px] uppercase tracking-widest font-semibold">{c.label}</div>
              <div className="text-2xl font-heading font-semibold tabular-nums mt-1">{counts[c.key] || 0}</div>
            </button>
          ))}
        </div>
        <div className="bg-white border border-slate-200 overflow-x-auto">
          {loading ? (
            <div className="p-6 text-sm text-slate-500">Loading care plans…</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="care-plans-empty">No care plans match this filter.</div>
          ) : (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-xs text-slate-700 border-b border-slate-200">
                  <th className="text-left px-3 py-2 font-semibold">Service user</th>
                  <th className="text-left px-3 py-2 font-semibold">Plan type</th>
                  <th className="text-left px-3 py-2 font-semibold">Title</th>
                  <th className="text-left px-3 py-2 font-semibold">Status</th>
                  <th className="text-left px-3 py-2 font-semibold">Reviewed</th>
                  <th className="text-left px-3 py-2 font-semibold">Next Review</th>
                  <th className="text-right px-3 py-2 font-semibold w-10"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100" data-testid={`care-plan-row-${p.id}`}>
                    <td className="px-3 py-2"><Link to={`/clients/${p.client_id}/care-planning`} className="hover:text-indigo-700 hover:underline">{p.client_name}</Link></td>
                    <td className="px-3 py-2 text-xs">{p.plan_type}</td>
                    <td className="px-3 py-2 text-sm">{p.title}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] uppercase tracking-widest border px-1.5 py-0.5 ${p.review_status === "overdue" ? "bg-red-100 text-red-800 border-red-300" : p.review_status === "due_soon" ? "bg-amber-100 text-amber-800 border-amber-300" : p.review_status === "current" ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-slate-100 text-slate-700 border-slate-300"}`}>
                        {p.review_status.replace("_", " ")}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs tabular-nums">{p.reviewed_date || "—"}</td>
                    <td className="px-3 py-2 text-xs tabular-nums">{p.next_review_date || "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => saveAsTemplate(p)}
                        title="Save this plan as a reusable template"
                        data-testid={`btn-save-template-${p.id}`}
                        className="text-slate-400 hover:text-emerald-700"
                      >
                        <Save className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <TemplatePickerDialog open={tplOpen} onClose={() => setTplOpen(false)} onCreated={load} />
    </div>
  );
}

export function RiskRegister() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sevFilter, setSevFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/dashboard/risk-register")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.risks.filter((r) =>
      (sevFilter === "all" || r.severity === sevFilter) &&
      (typeFilter === "all" || r.assessment_type === typeFilter)
    );
  }, [data, sevFilter, typeFilter]);

  const bySev = data?.by_severity || {};
  const byType = Object.keys(data?.by_type || {}).sort();

  const exportPdf = async () => {
    setPdfBusy(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      await downloadBlob("/reports/risk-register.pdf", `ayobell-risk-register-${today}.pdf`);
      toast.success("Risk Register downloaded — CQC evidence pack ready");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPdfBusy(false); }
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="h-14 border-b border-slate-200 bg-white px-6 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Governance</div>
          <h1 className="font-heading text-xl font-semibold" data-testid="risk-register-title">Site Risk Register</h1>
        </div>
        <Button onClick={exportPdf} disabled={pdfBusy} data-testid="btn-risk-register-pdf" className="bg-slate-900 hover:bg-slate-800 h-9 text-xs">
          <FileDown className="w-3.5 h-3.5 mr-1.5" /> {pdfBusy ? "Preparing…" : "Export PDF for CQC"}
        </Button>
      </div>
      <div className="p-4 lg:p-6 space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="risk-cards">
          {["critical", "high", "medium", "low"].map((s) => (
            <button key={s} onClick={() => setSevFilter(sevFilter === s ? "all" : s)} data-testid={`risk-card-${s}`} className={`border p-4 text-left ${SEV_TONE[s]} ${sevFilter === s ? "ring-2 ring-slate-900 ring-inset" : ""}`}>
              <div className="text-[10px] uppercase tracking-widest font-semibold">{s}</div>
              <div className="text-2xl font-heading font-semibold tabular-nums mt-1">{bySev[s] || 0}</div>
            </button>
          ))}
        </div>
        <div className="bg-white border border-slate-200 p-3 flex items-center gap-2 flex-wrap" data-testid="risk-filters">
          <Filter className="w-3 h-3 text-slate-500" />
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Filter by type</span>
          <button onClick={() => setTypeFilter("all")} data-testid="risk-type-all" className={`text-xs border px-2 py-1 ${typeFilter === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300"}`}>All ({data?.total || 0})</button>
          {byType.map((t) => (
            <button key={t} onClick={() => setTypeFilter(typeFilter === t ? "all" : t)} data-testid={`risk-type-${t}`} className={`text-xs border px-2 py-1 ${typeFilter === t ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300"}`}>
              {t} ({data?.by_type?.[t] || 0})
            </button>
          ))}
        </div>
        <div className="bg-white border border-slate-200 overflow-x-auto">
          {loading ? (
            <div className="p-6 text-sm text-slate-500">Loading risk register…</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="risk-empty">No risks match the current filters.</div>
          ) : (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-xs text-slate-700 border-b border-slate-200">
                  <th className="text-left px-3 py-2 font-semibold">Service user</th>
                  <th className="text-left px-3 py-2 font-semibold">Severity</th>
                  <th className="text-left px-3 py-2 font-semibold">Type</th>
                  <th className="text-left px-3 py-2 font-semibold">Title</th>
                  <th className="text-left px-3 py-2 font-semibold">Controls</th>
                  <th className="text-left px-3 py-2 font-semibold">Residual</th>
                  <th className="text-left px-3 py-2 font-semibold">Next Review</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 align-top" data-testid={`risk-row-${r.id}`}>
                    <td className="px-3 py-2"><Link to={`/clients/${r.client_id}/alerts`} className="hover:text-red-700 hover:underline">{r.client_name}</Link></td>
                    <td className="px-3 py-2"><span className={`text-[10px] uppercase tracking-widest border px-1.5 py-0.5 ${SEV_TONE[r.severity] || "bg-slate-100 text-slate-700 border-slate-300"}`}>{r.severity || "n/a"}</span></td>
                    <td className="px-3 py-2 text-xs">{r.assessment_type || "—"}</td>
                    <td className="px-3 py-2 text-sm font-medium">{r.title}</td>
                    <td className="px-3 py-2 text-xs text-slate-700 max-w-xs truncate" title={r.controls || ""}>{r.controls || "—"}</td>
                    <td className="px-3 py-2"><span className={`text-[10px] uppercase tracking-widest border px-1.5 py-0.5 ${SEV_TONE[r.residual_risk] || "bg-slate-50 text-slate-500 border-slate-200"}`}>{r.residual_risk || "n/a"}</span></td>
                    <td className="px-3 py-2 text-xs tabular-nums">{r.next_review_date || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
